import { IUserProfile } from "@redbelly-tokeniser/mystake-api-sdk";
import { FeatureIds, IUserCompanyRoles, Roles } from "@redbelly-tokeniser/mystake-common-sdk";
import { CompanyFeatures } from "../components/companies/pdtiFeatureFlags";
import { KycProvider } from "./events";

/** Internal/staff accounts, identified by a company email domain — never sent to Mixpanel, only the boolean flag is. */
const INTERNAL_EMAIL_DOMAINS = ["@redbelly.network", "@redbellynetwork.com"];

export type CompanyRole = "super_admin" | "admin" | "approver" | "investor" | "external_investor" | "none";

export interface SuperProperties {
  platform: "web";
  app_version: string;
  /** Deployment environment (e.g. "local", "qa", "uat", "production"); lets a single Mixpanel project filter reporting by environment. */
  environment: string;
  company_id: string | null;
  company_role: CompanyRole;
  is_investor: boolean;
  is_external_investor: boolean;
  feature_pdti_full: boolean;
  feature_fundraise: boolean;
  feature_approvals: boolean;
  kyc_provider: KycProvider;
  internal_user: boolean;
}

/** Falls back to "unknown" so a missing/unset runtime config value is still a valid, filterable segment rather than absent from events entirely. */
export function resolveEnvironmentName(): string {
  return window.config?.ENVIRONMENT_NAME || "unknown";
}

export function resolveCompanyRole(roles: IUserCompanyRoles | undefined): CompanyRole {
  if (!roles) return "none";
  if (roles.isSuperAdmin) return "super_admin";
  if (roles.isAdmin) return "admin";
  if (roles.isApprover) return "approver";
  if (roles.isExternalInvestor) return "external_investor";
  if (roles.isInvestor) return "investor";
  return "none";
}

/** Staff/test accounts are flagged so production reporting can exclude them (FR-005). The email itself is never sent. */
export function isInternalUser(user: IUserProfile | null): boolean {
  if (!user?.emails?.length) return false;
  return user.emails.some((entry) =>
    INTERNAL_EMAIL_DOMAINS.some((domain) => entry.email?.toLowerCase().endsWith(domain))
  );
}

export function resolvePrimaryAccountType(user: IUserProfile | null): "admin" | "investor" | "mixed" | "nominee" {
  if (!user) return "investor";
  const nonInvestorRoles = user.roles?.some((role) => role !== Roles.SHAREHOLDER && role !== Roles.EXTERNAL_INVESTOR);
  if (user.isInvestor && nonInvestorRoles) return "mixed";
  if (user.roles?.includes(Roles.NOMINEE) || user.roles?.includes(Roles.DIRECTOR)) return "nominee";
  return user.isInvestor ? "investor" : "admin";
}

export function buildSuperProperties(
  user: IUserProfile | null,
  companyId: string | null,
  roles: IUserCompanyRoles | undefined,
  features: CompanyFeatures | undefined,
  appVersion: string
): SuperProperties {
  return {
    platform: "web",
    app_version: appVersion,
    environment: resolveEnvironmentName(),
    company_id: companyId || null,
    company_role: resolveCompanyRole(roles),
    is_investor: Boolean(user?.isInvestor),
    is_external_investor: Boolean(user?.isExternalInvestor),
    feature_pdti_full: Boolean(features?.[FeatureIds.PDTI_FULL]),
    feature_fundraise: Boolean(features?.[FeatureIds.FUNDRAISE_FEATURES]),
    feature_approvals: Boolean(features?.[FeatureIds.APPROVALS]),
    kyc_provider: "manual",
    internal_user: isInternalUser(user),
  };
}
